import { useCallback } from 'react'

import { useTemplateContext } from '../../context/templateContext'
import { useLazyLayerMetaModel } from '../../context/useLazyLayerMetaModel'
import {
  type FPlusExpressionValue,
  isNotRelevantField,
  type NonConfigurationSimpleValueField,
  type ValueSetupFieldValue,
} from '../../types/templateModel'
import { generateEmptyFieldValue } from '../../uiModels/createEmptyFieldHelpers'
import { createLiteralTypeInformation } from '../fplusExpression/expressionType'
import { TemplateFieldExpression } from '../templateFieldExpression/TemplateFieldExpression'

type Props = {
  field: NonConfigurationSimpleValueField
  property: 'required' | 'requiredInModelInstance'
}

export const TemplateFieldRequired = ({ field, property }: Props) => {
  const { templateVersion } = useTemplateContext()
  const metaModel = useLazyLayerMetaModel(templateVersion?.layer?.layerId)

  const fieldValue = (field.value ??
    generateEmptyFieldValue({
      field,
      meta: metaModel,
    })) as ValueSetupFieldValue

  const handleCodeChanged = useCallback(
    (nextValue: FPlusExpressionValue) => {
      const nextValueSetupValue = { ...fieldValue }
      delete nextValueSetupValue[property]

      const update = field.update as (v: ValueSetupFieldValue) => Promise<void>
      return update({
        ...nextValueSetupValue,
        ...(nextValue.fplusSource
          ? {
              [property]: {
                ...fieldValue[property],
                expression: nextValue,
                origin: fieldValue[property]?.path,
              },
            }
          : {}),
      })
    },
    [field.update, fieldValue, property],
  )

  const fullPath = `${field.path}.${property}`
  const expression = fieldValue[property]?.expression

  return (
    <TemplateFieldExpression
      expression={expression}
      isExpanded
      onChange={!isNotRelevantField(field) ? handleCodeChanged : undefined}
      path={fullPath}
      fallbackCode=""
      typeInformation={createLiteralTypeInformation({
        literalType: 'boolean',
        source: expression?.fplusSource,
      })}
      fromOrigin={field.value?.[property]?.origin}
    />
  )
}
